import type { RealtimeVoiceFeatureSettings, RealtimeVoiceFeatureSettingsWrite, DesktopRealtimeVoiceAvailability, DesktopVoiceAvailability } from '../../shared-types/src/realtime.js';
import type { RealtimeVoiceAdminView, RealtimeVoiceAdminWrite, TenantVoiceAdminView, TenantVoicePolicyWrite, VoiceFeatureSettings, VoiceFeatureSettingsWrite, VoiceModelAdminWrite } from "../../shared-types/src/realtime.js";
import type { SessionProtocol, SessionCapabilities, SessionRevokeInput, RotatingTokenPair } from '../../shared-types/src/session.js';
import type { RecommendedModelProfileView, RecommendedModelProfileWrite } from '../../shared-types/src/recommended-models.js';
import type { ModelThinkingBootstrapOptions } from '../../shared-types/src/model-thinking.js';
// fetch-based `ServiceClient` implementation. Used by admin-web in the browser
// and by any test that needs to exercise the wire surface (the Idempotency-Key
// auto-injection test in `tests/admin-web/admin-web.test.ts` is the
// canonical case).
//
// Hard contract:
//   - The route is looked up in `SERVICE_ROUTES`. Path params (`:id`, etc.)
//     are interpolated from the call's argument object.
//   - `Authorization: Bearer <token>` is sent whenever the caller supplies an
//     access token; the deprecated `AUTH_REQUIRED_METHODS` view is not used.
//   - `Idempotency-Key` is auto-injected for admin writes (the service-api
//     rejects any admin write without one). The caller may still pass an
//     explicit key in the request body — we only inject when the caller
//     didn't supply one (in practice every admin write method takes an
//     `idempotencyKey` arg, so the body always carries it; we still set the
//     header as a belt-and-suspenders duplicate of the body key so the
//     service-api middleware reads it from a single place).
//   - Non-2xx responses are thrown as `ServiceClientError` carrying status
//     and parsed body, so admin screens can surface the message.

import {
  SERVICE_ROUTES,
  type CreatedUserResult,
  type LoginStartResult,
  type ModelRouteAdminView,
  type PartnerCustomModelSummary,
  type PartnerCustomModelDeployment,
  type PartnerCustomModelTestRequest,
  type UpdatePartnerCustomModelRequest,
  type UpdatePartnerCustomModelResult,
  type PartnerCustomModelTestResult,
  type PartnerCustomModelWrite,
  type ServiceClient,
  type TokenPair,
  type AccessTokenResult,
  type ActAsSessionResult,
} from './index.js';
import { ROUTE_TABLE } from '../../shared-types/src/routes.js';
import type {
  AllocationBatchInput,
  AllocationBatchResult,
  AllocationInput,
  CapabilitySourceRegisterInput,
  MemberQuotaDefaultInput,
} from './index.js';
import type {
  CapabilitySourceAssignment,
  CapabilitySourceRecord,
} from '../../shared-types/src/index.js';
import type {
  AdminAuditEvent,
  AdminGroupView,
  AdminOverview,
  AdminUserView,
  AutoRoutingPreviewRequest,
  AutoRoutingPreviewResult,
  AutoRoutingDecisionsView,
  AutoRoutingProfileView,
  AutoRoutingProfileWrite,
  AgentPermissionMode,
  Announcement,
  AnnouncementListQuery,
  CreateAnnouncementRequest,
  MarkAllAnnouncementsReadResult,
  MeAnnouncementsResponse,
  PatchAnnouncementRequest,
  AllocationOverview,
  AuditListQuery,
  CreditAllocation,
  CreditLimitView,
  EffectiveCreditLimitsRequest,
  EffectiveCreditLimitsView,
  UpsertCreditLimitRequest,
  CreateQuotaProfileRequest,
  MembershipQuotaAssignment,
  QuotaProfileAssignmentPreview,
  QuotaProfileLimitInput,
  QuotaProfileListView,
  QuotaProfileView,
  UpdateQuotaProfileRequest,
  UserQuotaAssignmentQuery,
  UserQuotaAssignmentView,
  MemberQuotaDefault,
  DesktopBootstrap,
  DiagnosticBundleListQuery,
  DiagnosticBundleView,
  RequiredConsent,
  DesktopModelView,
  DesktopPlatform,
  DownloadChannel,
  DownloadManifest,
  ExtensionReleaseView,
  ExtensionManifest,
  GatewayBackend,
  GatewayCompatibility,
  Group,
  InvitedUser,
  ListQuery,
  ModelRoute,
  ModelRoutingPolicy,
  OrganizationSsoConnectionAdmin,
  OrganizationSsoConnectionPatch,
  OrganizationSsoConnectionTestInput,
  OrganizationSsoConnectionTestResult,
  OrganizationSsoConnectionWrite,
  OrganizationSsoDiscovery,
  OrganizationSsoIdentifierType,
  OrganizationSsoMode,
  Paginated,
  Partner,
  PartnerAdminView,
  PartnerOverview,
  PlatformAdminView,
  PartnerWebSearchUsageSummary,
  PasswordLoginInput,
  PasswordResetCodeAccepted,
  PasswordResetCodeConfirmInput,
  PasswordResetCodeRequestInput,
  PasswordResetCodeResendInput,
  PasswordResetCodeResent,
  PasswordResetCodeVerifyInput,
  PasswordResetCodeVerified,
  PlatformProviderKeyProvider,
  PlatformProviderModelDefinition,
  PlatformProviderModelDeployment,
  PlatformProviderModelListQuery,
  PlatformProviderModelTestResult,
  CreatePlatformProviderModelRequest,
  DeployPlatformProviderModelRequest,
  DeployPlatformProviderModelResult,
  BulkDeployPlatformProviderModelsRequest,
  BulkDeployPlatformProviderModelsResult,
  PublishPlatformProviderModelRatesRequest,
  PublishPlatformProviderModelRatesResult,
  UpdatePlatformProviderModelRequest,
  PlatformWebSearchKeySummary,
  ProviderKeyRegistrationResult,
  ProviderKeyRemovalResult,
  ProviderKeySummary,
  ProviderKeyTestResult,
  TestLiteLLMConnectionInput,
  TestProviderKeyInput,
  PolicyContextSnapshot,
  QuotaPolicy,
  ServiceRole,
  SocialLoginMode,
  SocialProviderConfig,
  SocialProviderKind,
  SocialProviderPublic,
  SignupConfig,
  SignupAccepted,
  SignupInput,
  Tenant,
  TenantPartnerTransferResult,
  TenantSelfSignupResult,
  TenantOrganizationSsoConfig,
  TenantAssignment,
  TenantLoginDefaultModelView,
  TenantMcpConnectors,
  TenantMediaRoute,
  TenantMediaRouteSummary,
  TenantModelAccess,
  TenantSocialConfig,
  TenantSocialProvider,
  TenantWebSearchBackend,
  TenantWebSearchBackendSummary,
  DeploymentProfile,
  DefaultSignupTenantSetting,
  CreditRateTableView,
  CreditRateSettingsView,
  PutCreditRateSettingsRequest,
  PutCreditRateSettingsResult,
  UsageChainVerification,
  UsageEventView,
  UsageQuery,
  CreditSettlementQuery,
  CreditSettlementReport,
  UsageSeriesPoint,
  UsageSeriesQuery,
  UsageSummaryView,
  UserListQuery,
  BillingHistoryPage,
  CardRegistrationStart,
  CardRegistrationView,
  CreateCreditOrderInput,
  CreditPurchaseOrderView,
  MyBillingSummary,
  ResolvedCreditOffer,
  VelugaModelCatalogItem,
  WebSearchNativeProvider,
} from '../../shared-types/src/index.js';
import type { GatewayBackendAdminView } from './index.js';
import type { MergePolicyInput } from '../../policy-service/src/merge.js';
import { createHttpDownloads } from './http-downloads.js';

export interface HttpServiceClientOptions {
  baseUrl: string;
  getAccessToken: () => string | undefined;
  fetchImpl?: typeof fetch;
  randomUUID?: () => string;
  requestTimeoutMs?: number;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export class ServiceClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown
  ) {
    super(`service client error ${status}`);
    this.name = 'ServiceClientError';
  }
}

/** @deprecated Use ROUTE_TABLE descriptor write metadata instead. */
const ADMIN_WRITE_ROUTES: ReadonlySet<keyof typeof ROUTE_TABLE> = new Set(
  Object.entries(ROUTE_TABLE)
    .filter(([, route]) => 'write' in route && route.write === true)
    .map(([key]) => key as keyof typeof ROUTE_TABLE)
);

/** Append defined query params (`?limit=&offset=&q=...`) to a route path. */
function withQuery(path: string, query: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value as string | number));
  }
  // The caller asked for envelope mode (it passed a query object), so at least
  // one recognized param must reach the server — otherwise it would answer
  // with the legacy array while the caller's types expect Paginated<T>.
  // offset=0 is the server default, i.e. semantically a no-op.
  if (search.size === 0) search.set('offset', '0');
  return `${path}?${search.toString()}`;
}

function defaultRandomUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    return crypto.randomUUID();
  return `idem_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export class HttpServiceClient implements ServiceClient {
  private readonly fetcher: typeof fetch;
  private readonly randomUUID: () => string;
  private readonly downloads = createHttpDownloads({
    call: (routeName, body, accessToken) => this.call(routeName, body, accessToken),
    rawCall: (method, path, body, accessToken) => this.rawCall(method, path, body, accessToken),
    interpolate: (path, params) => this.interpolate(path, params),
    withQuery,
  });

  constructor(private readonly opts: HttpServiceClientOptions) {
    this.fetcher = opts.fetchImpl ?? ((input, init) => fetch(input, init));
    this.randomUUID = opts.randomUUID ?? defaultRandomUUID;
  }

  // ── auth ────────────────────────────────────────────────────────────

  async sessionCapabilities(): Promise<SessionCapabilities> { return await this.rawCall('GET', SERVICE_ROUTES.sessionCapabilities.path, undefined, undefined) as SessionCapabilities; }
  async refreshSession(refreshToken: string): Promise<RotatingTokenPair> { return await this.rawCall('POST', SERVICE_ROUTES.sessionRefresh.path, { refreshToken }, undefined) as RotatingTokenPair; }
  async revokeSession(input: SessionRevokeInput): Promise<{ ok: true }> { return await this.rawCall('POST', SERVICE_ROUTES.sessionRevoke.path, input, undefined) as { ok: true }; }

  async loginStart(
    invite?: { tenantId: string; userId: string; inviteToken: string; sessionProtocol?: SessionProtocol },
    opts?: { providerId?: string; sessionProtocol?: SessionProtocol }
  ): Promise<LoginStartResult> {
    const body = opts ? { ...(invite ?? {}), ...opts } : invite;
    const result = await this.call('loginStart', body);
    return result as LoginStartResult;
  }

  async listSocialProviders(
    tenantHint?: string
  ): Promise<SocialProviderPublic[]> {
    const path =
      SERVICE_ROUTES.socialProviders.path +
      (tenantHint ? `?tenantHint=${encodeURIComponent(tenantHint)}` : '');
    // Public endpoint — no access token.
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      undefined
    )) as SocialProviderPublic[];
  }

  async getSignupConfig(): Promise<SignupConfig> {
    return (await this.rawCall('GET', SERVICE_ROUTES.signupConfigGet.path, undefined, undefined)) as SignupConfig;
  }

  async signup(input: SignupInput): Promise<SignupAccepted> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.signup.path,
      input,
      undefined
    )) as SignupAccepted;
  }

  async requestPasswordResetCode(
    input: PasswordResetCodeRequestInput
  ): Promise<PasswordResetCodeAccepted> {
    return (await this.rawCall(
      'POST', SERVICE_ROUTES.passwordResetCodeRequest.path, input, undefined
    )) as PasswordResetCodeAccepted;
  }

  async resendPasswordResetCode(
    input: PasswordResetCodeResendInput
  ): Promise<PasswordResetCodeResent> {
    return (await this.rawCall(
      'POST', SERVICE_ROUTES.passwordResetCodeResend.path, input, undefined
    )) as PasswordResetCodeResent;
  }

  async verifyPasswordResetCode(
    input: PasswordResetCodeVerifyInput
  ): Promise<PasswordResetCodeVerified> {
    return (await this.rawCall(
      'POST', SERVICE_ROUTES.passwordResetCodeVerify.path, input, undefined
    )) as PasswordResetCodeVerified;
  }

  async confirmPasswordResetCode(input: PasswordResetCodeConfirmInput): Promise<void> {
    await this.rawCall(
      'POST', SERVICE_ROUTES.passwordResetCodeConfirm.path, input, undefined
    );
  }

  async listOrganizationSsoConnections(
    tenantHint?: string
  ): Promise<OrganizationSsoDiscovery> {
    const path =
      SERVICE_ROUTES.organizationSsoConnections.path +
      (tenantHint ? `?tenantHint=${encodeURIComponent(tenantHint)}` : '');
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      undefined
    )) as OrganizationSsoDiscovery;
  }

  async organizationSsoLoginStart(input: {
    sessionProtocol?: SessionProtocol;
    connectionId: string;
    identifierType?: OrganizationSsoIdentifierType;
    identifier?: string;
  }): Promise<LoginStartResult> {
    return (await this.call(
      'organizationSsoLoginStart',
      input
    )) as LoginStartResult;
  }

  async loginComplete(
    input: string | { tenantId: string; userId: string; inviteToken: string; sessionProtocol?: SessionProtocol }
  ): Promise<TokenPair> {
    const arg = typeof input === 'string' ? { loginHandle: input } : input;
    return (await this.call('loginComplete', arg)) as TokenPair;
  }

  async loginWithPassword(input: PasswordLoginInput): Promise<TokenPair> {
    return (await this.call('loginPassword', input)) as TokenPair;
  }

  async changePassword(
    accessToken: string,
    oldPassword: string,
    newPassword: string
  ): Promise<TokenPair | void> {
    const result = await this.call(
      'changePassword',
      { oldPassword, newPassword },
      accessToken
    ) as Partial<TokenPair>;
    // Older servers only return { ok: true }.
    return typeof result.accessToken === 'string' && typeof result.refreshToken === 'string' && typeof result.expiresAt === 'string'
      ? result as TokenPair : undefined;
  }

  async keepPassword(accessToken: string): Promise<void> {
    await this.rawCall(
      'POST',
      SERVICE_ROUTES.passwordKeep.path,
      undefined,
      accessToken
    );
  }

  async refresh(refreshToken: string): Promise<AccessTokenResult> {
    return (await this.call('refresh', { refreshToken })) as AccessTokenResult;
  }

  // ── self ────────────────────────────────────────────────────────────

  async bootstrap(accessToken: string, options?: ModelThinkingBootstrapOptions): Promise<DesktopBootstrap> {
    return (await this.rawCall(
      SERVICE_ROUTES.bootstrap.method, SERVICE_ROUTES.bootstrap.path,
      undefined,
      accessToken, undefined,
      options?.modelThinkingVersion === 2 ? { 'X-Veluga-Model-Thinking-Version': '2' } : {}
    )) as DesktopBootstrap;
  }

  async getMyBillingSummary(accessToken: string): Promise<MyBillingSummary> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.meBilling.path,
      undefined,
      accessToken
    )) as MyBillingSummary;
  }

  async listMyCreditProducts(accessToken: string): Promise<ResolvedCreditOffer[]> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.meBillingProducts.path,
      undefined,
      accessToken
    )) as ResolvedCreditOffer[];
  }

  async startCardRegistration(accessToken: string): Promise<CardRegistrationStart> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.meBillingCardRegistrationStart.path,
      undefined,
      accessToken
    )) as CardRegistrationStart;
  }

  async getCardRegistration(
    accessToken: string,
    registrationId: string
  ): Promise<CardRegistrationView> {
    const path = this.interpolate(SERVICE_ROUTES.meBillingCardRegistrationGet.path, {
      registrationId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as CardRegistrationView;
  }

  async deleteMyCard(accessToken: string): Promise<void> {
    await this.rawCall(
      'DELETE',
      SERVICE_ROUTES.meBillingCardDelete.path,
      undefined,
      accessToken
    );
  }

  async createCreditOrder(
    accessToken: string,
    input: CreateCreditOrderInput
  ): Promise<CreditPurchaseOrderView> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.meBillingOrdersCreate.path,
      input,
      accessToken,
      input.idempotencyKey
    )) as CreditPurchaseOrderView;
  }

  async payCreditOrder(
    accessToken: string,
    orderId: string
  ): Promise<CreditPurchaseOrderView> {
    const path = this.interpolate(SERVICE_ROUTES.meBillingOrderPay.path, {
      orderId,
    });
    return (await this.rawCall(
      'POST',
      path,
      undefined,
      accessToken
    )) as CreditPurchaseOrderView;
  }

  async getCreditOrder(
    accessToken: string,
    orderId: string
  ): Promise<CreditPurchaseOrderView> {
    const path = this.interpolate(SERVICE_ROUTES.meBillingOrderGet.path, {
      orderId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as CreditPurchaseOrderView;
  }

  async listMyBillingHistory(
    accessToken: string,
    input: { page: number; pageSize: number }
  ): Promise<BillingHistoryPage> {
    const path = withQuery(
      this.interpolate(SERVICE_ROUTES.meBillingHistory.path, {}),
      input
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as BillingHistoryPage;
  }
  async acceptConsents(
    accessToken: string,
    input: { consents: RequiredConsent[] }
  ): Promise<{ pendingConsents: RequiredConsent[] }> {
    return (await this.call('meConsentsAccept', input, accessToken)) as {
      pendingConsents: RequiredConsent[];
    };
  }
  async uploadDiagnosticBundle(
    accessToken: string,
    bytes: Uint8Array,
    meta: {
      deviceId: string;
      appVersion: string;
      platform: 'win32' | 'darwin' | 'linux';
      collectedAt: string;
      logFileCount: number;
    }
  ): Promise<{ id: string; uploadedAt: string }> {
    return (await this.rawBinaryCall(
      SERVICE_ROUTES.meDiagnosticBundleUpload.method,
      SERVICE_ROUTES.meDiagnosticBundleUpload.path,
      bytes,
      accessToken,
      {
        'x-veluga-device-id': meta.deviceId,
        'x-veluga-app-version': meta.appVersion,
        'x-veluga-platform': meta.platform,
        'x-veluga-collected-at': meta.collectedAt,
        'x-veluga-log-file-count': String(meta.logFileCount)
      },
      120_000
    )) as { id: string; uploadedAt: string };
  }
  async catalog(accessToken: string): Promise<DesktopModelView[]> {
    return (await this.call(
      'catalog',
      undefined,
      accessToken
    )) as DesktopModelView[];
  }
  async availableModels(accessToken: string): Promise<DesktopModelView[]> {
    return (await this.call(
      'availableModels',
      undefined,
      accessToken
    )) as DesktopModelView[];
  }
  async policiesEffective(accessToken: string): Promise<PolicyContextSnapshot> {
    return (await this.call(
      'policiesEffective',
      undefined,
      accessToken
    )) as PolicyContextSnapshot;
  }
  async postClientEvent(
    accessToken: string,
    event: Record<string, unknown>
  ): Promise<void> {
    await this.call('auditClientEvents', event, accessToken);
  }
  // ── downloads ──────────────────────────────────────────────────────

  async downloadChannels(accessToken: string): Promise<DownloadChannel[]> {
    return this.downloads.downloadChannels(accessToken);
  }
  async downloadLatest(
    accessToken: string,
    channel: DownloadChannel,
    platform: DesktopPlatform
  ): Promise<DownloadManifest> {
    return this.downloads.downloadLatest(accessToken, channel, platform);
  }
  async extensionLatest(
    accessToken: string,
    extensionId: string,
    platform: 'win32' | 'darwin',
    request: { appVersion: string; protocolVersion: number }
  ): Promise<ExtensionManifest> {
    return this.downloads.extensionLatest(accessToken, extensionId, platform, request);
  }

  // ── gateway capabilities ───────────────────────────────────────────

  async gatewayCapabilities(
    accessToken: string
  ): Promise<GatewayCompatibility> {
    return (await this.call(
      'gatewayCapabilities',
      undefined,
      accessToken
    )) as GatewayCompatibility;
  }

  // ── platform ───────────────────────────────────────────────────────

  async platformLoginWithPassword(input: {
    sessionProtocol?: SessionProtocol;
    email: string;
    password: string;
  }): Promise<TokenPair> {
    return (await this.call('platformLoginPassword', input)) as TokenPair;
  }

  async listTenants(accessToken: string): Promise<Tenant[]>;
  async listTenants(
    accessToken: string,
    query: ListQuery
  ): Promise<Paginated<Tenant>>;
  async listTenants(
    accessToken: string,
    query?: ListQuery
  ): Promise<Tenant[] | Paginated<Tenant>> {
    if (query) {
      const path = withQuery(SERVICE_ROUTES.tenantsList.path, query);
      return (await this.rawCall(
        'GET',
        path,
        undefined,
        accessToken
      )) as Paginated<Tenant>;
    }
    return (await this.call('tenantsList', undefined, accessToken)) as Tenant[];
  }

  async createTenant(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      partnerId?: string;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return (await this.call(
      'tenantsCreate',
      input,
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }

  async deleteTenant(
    accessToken: string,
    input: { tenantId: string; confirmName: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.tenantsDelete.path, {
      tenantId: input.tenantId,
    });
    await this.rawCall(
      'DELETE',
      path,
      { confirmName: input.confirmName },
      accessToken,
      input.idempotencyKey
    );
  }

  async transferTenantPartner(
    accessToken: string,
    input: {
      tenantId: string;
      partnerId: string | null;
      confirmName?: string;
      dryRun?: boolean;
      idempotencyKey: string;
    }
  ): Promise<TenantPartnerTransferResult> {
    const path = this.interpolate(SERVICE_ROUTES.tenantPartnerTransfer.path, {
      tenantId: input.tenantId,
    });
    return (await this.rawCall(
      'PUT',
      path,
      {
        partnerId: input.partnerId,
        ...(input.confirmName !== undefined ? { confirmName: input.confirmName } : {}),
        ...(input.dryRun !== undefined ? { dryRun: input.dryRun } : {}),
      },
      accessToken,
      input.idempotencyKey
    )) as TenantPartnerTransferResult;
  }

  async actAsTenant(
    accessToken: string,
    tenantId: string
  ): Promise<ActAsSessionResult> {
    const path = this.interpolate(SERVICE_ROUTES.platformActAs.path, {
      tenantId,
    });
    return (await this.rawCall(
      'POST',
      path,
      undefined,
      accessToken
    )) as ActAsSessionResult;
  }

  async registerProviderKey(
    accessToken: string,
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      apiKey: string;
      baseUrl?: string;
      litellmBaseUrl?: string;
      idempotencyKey: string;
    }
  ): Promise<ProviderKeyRegistrationResult> {
    return (await this.call(
      'providerKeysRegister',
      input,
      accessToken,
      input.idempotencyKey
    )) as ProviderKeyRegistrationResult;
  }

  async listPlatformProviderModels(
    accessToken: string,
    query: PlatformProviderModelListQuery = {}
  ): Promise<Paginated<PlatformProviderModelDefinition>> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.platformProviderModelsList.path, query),
      undefined,
      accessToken
    )) as Paginated<PlatformProviderModelDefinition>;
  }

  async getPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string
  ): Promise<PlatformProviderModelDefinition> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsGet.path, {
      provider,
      modelKey,
    });
    return (await this.rawCall('GET', path, undefined, accessToken)) as PlatformProviderModelDefinition;
  }

  async createPlatformProviderModel(
    accessToken: string,
    input: CreatePlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<PlatformProviderModelDefinition> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.platformProviderModelsCreate.path,
      body,
      accessToken,
      idempotencyKey
    )) as PlatformProviderModelDefinition;
  }

  async updatePlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: UpdatePlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<PlatformProviderModelDefinition> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsUpdate.path, {
      provider,
      modelKey,
    });
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall('PUT', path, body, accessToken, idempotencyKey)) as PlatformProviderModelDefinition;
  }

  async disablePlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    idempotencyKey: string
  ): Promise<PlatformProviderModelDefinition> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsDisable.path, {
      provider,
      modelKey,
    });
    return (await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      idempotencyKey
    )) as PlatformProviderModelDefinition;
  }

  async testPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string
  ): Promise<PlatformProviderModelTestResult> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsTest.path, {
      provider,
      modelKey,
    });
    return (await this.rawCall('POST', path, { tenantId }, accessToken)) as PlatformProviderModelTestResult;
  }

  async deployPlatformProviderModel(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: DeployPlatformProviderModelRequest & { idempotencyKey: string }
  ): Promise<DeployPlatformProviderModelResult> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsDeploy.path, {
      provider,
      modelKey,
    });
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall('POST', path, body, accessToken, idempotencyKey)) as DeployPlatformProviderModelResult;
  }

  async bulkDeployPlatformProviderModels(
    accessToken: string,
    tenantId: string,
    input: BulkDeployPlatformProviderModelsRequest & { idempotencyKey: string }
  ): Promise<BulkDeployPlatformProviderModelsResult> {
    const path = this.interpolate(SERVICE_ROUTES.tenantProviderModelsBulkDeploy.path, {
      tenantId,
    });
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'POST',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as BulkDeployPlatformProviderModelsResult;
  }

  async publishPlatformProviderModelRates(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    input: PublishPlatformProviderModelRatesRequest & { idempotencyKey: string }
  ): Promise<PublishPlatformProviderModelRatesResult> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsRates.path, {
      provider,
      modelKey,
    });
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall('POST', path, body, accessToken, idempotencyKey)) as PublishPlatformProviderModelRatesResult;
  }

  async revokePlatformProviderModelDeployment(
    accessToken: string,
    provider: PlatformProviderKeyProvider,
    modelKey: string,
    tenantId: string,
    idempotencyKey: string
  ): Promise<PlatformProviderModelDeployment> {
    const path = this.interpolate(SERVICE_ROUTES.platformProviderModelsRevoke.path, {
      provider,
      modelKey,
      tenantId,
    });
    return (await this.rawCall('DELETE', path, undefined, accessToken, idempotencyKey)) as PlatformProviderModelDeployment;
  }

  async getRealtimeVoiceSettings(accessToken: string): Promise<RealtimeVoiceFeatureSettings> {
    return await this.rawCall('GET', SERVICE_ROUTES.realtimeVoiceSettingsGet.path, undefined, accessToken) as RealtimeVoiceFeatureSettings;
  }
  async putRealtimeVoiceSettings(accessToken: string, input: RealtimeVoiceFeatureSettingsWrite, idempotencyKey: string): Promise<RealtimeVoiceFeatureSettings> {
    return await this.rawCall('PUT', SERVICE_ROUTES.realtimeVoiceSettingsPut.path, input, accessToken, idempotencyKey) as RealtimeVoiceFeatureSettings;
  }
  async getMyRealtimeVoice(accessToken: string, options: import('./index.js').VoiceRequestOptions = {}): Promise<DesktopRealtimeVoiceAvailability> {
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 5000);
    const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
    return await this.rawCall('GET', SERVICE_ROUTES.meRealtimeVoice.path, undefined, accessToken, undefined, {}, signal) as DesktopRealtimeVoiceAvailability;
  }
  async getMyVoiceAvailability(accessToken: string, options: import('./index.js').VoiceRequestOptions = {}): Promise<DesktopVoiceAvailability> {
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 5000);
    const signal = options.signal ? AbortSignal.any([timeout, options.signal]) : timeout;
    return await this.rawCall('GET', SERVICE_ROUTES.meVoiceAvailability.path, undefined, accessToken, undefined, {}, signal) as DesktopVoiceAvailability;
  }

  async getRealtimeVoice(accessToken: string, tenantId: string): Promise<RealtimeVoiceAdminView> {
    return await this.rawCall('GET', this.interpolate(SERVICE_ROUTES.realtimeVoiceGet.path, { tenantId }), undefined, accessToken) as RealtimeVoiceAdminView;
  }
  async putRealtimeVoice(accessToken: string, tenantId: string, input: RealtimeVoiceAdminWrite, idempotencyKey: string): Promise<RealtimeVoiceAdminView> {
    return await this.rawCall('PUT', this.interpolate(SERVICE_ROUTES.realtimeVoicePut.path, { tenantId }), input, accessToken, idempotencyKey) as RealtimeVoiceAdminView;
  }
  async getVoiceSettings(accessToken: string): Promise<VoiceFeatureSettings> {
    return await this.rawCall('GET', SERVICE_ROUTES.voiceSettingsGet.path, undefined, accessToken) as VoiceFeatureSettings;
  }
  async putVoiceSettings(accessToken: string, input: VoiceFeatureSettingsWrite, idempotencyKey: string): Promise<VoiceFeatureSettings> {
    return await this.rawCall('PUT', SERVICE_ROUTES.voiceSettingsPut.path, input, accessToken, idempotencyKey) as VoiceFeatureSettings;
  }
  async getTenantVoice(accessToken: string, tenantId: string): Promise<TenantVoiceAdminView> {
    return await this.rawCall('GET', this.interpolate(SERVICE_ROUTES.tenantVoiceGet.path, { tenantId }), undefined, accessToken) as TenantVoiceAdminView;
  }
  async putTenantVoice(accessToken: string, tenantId: string, input: TenantVoicePolicyWrite, idempotencyKey: string): Promise<TenantVoiceAdminView> {
    return await this.rawCall('PUT', this.interpolate(SERVICE_ROUTES.tenantVoicePut.path, { tenantId }), input, accessToken, idempotencyKey) as TenantVoiceAdminView;
  }
  async postTenantVoiceModel(accessToken: string, tenantId: string, input: VoiceModelAdminWrite, idempotencyKey: string): Promise<TenantVoiceAdminView> {
    return await this.rawCall('POST', this.interpolate(SERVICE_ROUTES.tenantVoiceModelsPost.path, { tenantId }), input, accessToken, idempotencyKey) as TenantVoiceAdminView;
  }

  async getRecommendedModelProfile(accessToken: string, tenantId: string): Promise<RecommendedModelProfileView> {
    return await this.rawCall('GET', this.interpolate(SERVICE_ROUTES.recommendedModelsGet.path, { tenantId }), undefined, accessToken) as RecommendedModelProfileView;
  }

  async putRecommendedModelProfile(accessToken: string, input: { tenantId: string; profile: RecommendedModelProfileWrite; expectedRevision: number; idempotencyKey: string }): Promise<RecommendedModelProfileView> {
    return await this.rawCall('PUT', this.interpolate(SERVICE_ROUTES.recommendedModelsPut.path, { tenantId: input.tenantId }),
      { profile: input.profile, expectedRevision: input.expectedRevision }, accessToken, input.idempotencyKey) as RecommendedModelProfileView;
  }

  async getAutoRoutingProfile(accessToken: string, tenantId: string): Promise<AutoRoutingProfileView> {
    const path = this.interpolate(SERVICE_ROUTES.autoRoutingGet.path, { tenantId });
    return (await this.rawCall('GET', path, undefined, accessToken)) as AutoRoutingProfileView;
  }

  async putAutoRoutingProfile(
    accessToken: string,
    input: { tenantId: string; profile: AutoRoutingProfileWrite; idempotencyKey: string }
  ): Promise<AutoRoutingProfileView> {
    const path = this.interpolate(SERVICE_ROUTES.autoRoutingPut.path, { tenantId: input.tenantId });
    return (await this.rawCall(
      'PUT',
      path,
      { profile: input.profile },
      accessToken,
      input.idempotencyKey,
    )) as AutoRoutingProfileView;
  }

  async previewAutoRouting(
    accessToken: string,
    input: { tenantId: string; preview: AutoRoutingPreviewRequest }
  ): Promise<AutoRoutingPreviewResult> {
    const path = this.interpolate(SERVICE_ROUTES.autoRoutingPreview.path, { tenantId: input.tenantId });
    return (await this.rawCall('POST', path, { preview: input.preview }, accessToken)) as AutoRoutingPreviewResult;
  }

  async listAutoRoutingDecisions(
    accessToken: string,
    tenantId: string,
    limit?: number
  ): Promise<AutoRoutingDecisionsView> {
    const path = withQuery(this.interpolate(SERVICE_ROUTES.autoRoutingDecisions.path, { tenantId }), { limit });
    return (await this.rawCall('GET', path, undefined, accessToken)) as AutoRoutingDecisionsView;
  }

  async getProviderKeys(accessToken: string, tenantId: string): Promise<ProviderKeySummary[]> {
    const path = this.interpolate(SERVICE_ROUTES.providerKeysList.path, {
      tenantId
    });
    return (await this.rawCall('GET', path, undefined, accessToken)) as ProviderKeySummary[];
  }

  async removeProviderKey(
    accessToken: string,
    input: {
      tenantId: string;
      provider: PlatformProviderKeyProvider;
      idempotencyKey: string;
    }
  ): Promise<ProviderKeyRemovalResult> {
    const path = this.interpolate(SERVICE_ROUTES.providerKeysRemove.path, {
      tenantId: input.tenantId,
      provider: input.provider,
    });
    return (await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    )) as ProviderKeyRemovalResult;
  }

  async listTenantWebSearchBackends(
    accessToken: string,
    tenantId: string
  ): Promise<TenantWebSearchBackendSummary[]> {
    const path = this.interpolate(SERVICE_ROUTES.webSearchBackendsList.path, {
      tenantId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantWebSearchBackendSummary[];
  }

  async listTenantMediaRoutes(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMediaRouteSummary[]> {
    const path = this.interpolate(SERVICE_ROUTES.mediaRoutesList.path, { tenantId });
    return (await this.rawCall('GET', path, undefined, accessToken)) as TenantMediaRouteSummary[];
  }

  async getPlatformWebSearchKey(
    accessToken: string
  ): Promise<PlatformWebSearchKeySummary> {
    return (await this.call(
      'platformWebSearchKey',
      undefined,
      accessToken
    )) as PlatformWebSearchKeySummary;
  }

  async setPlatformWebSearchKey(
    accessToken: string,
    input: { apiKey: string; idempotencyKey: string }
  ): Promise<PlatformWebSearchKeySummary> {
    return (await this.call(
      'platformWebSearchKeySet',
      { apiKey: input.apiKey },
      accessToken,
      input.idempotencyKey
    )) as PlatformWebSearchKeySummary;
  }

  async setTenantWebSearchBackend(
    accessToken: string,
    input: {
      tenantId: string;
      provider: WebSearchNativeProvider;
      status: TenantWebSearchBackend['status'];
      maxUsesDefault?: number;
      idempotencyKey: string;
    }
  ): Promise<TenantWebSearchBackendSummary> {
    const path = this.interpolate(SERVICE_ROUTES.webSearchBackendsSet.path, {
      tenantId: input.tenantId,
      provider: input.provider,
    });
    return (await this.rawCall(
      'PUT',
      path,
      { status: input.status, maxUsesDefault: input.maxUsesDefault },
      accessToken,
      input.idempotencyKey
    )) as TenantWebSearchBackendSummary;
  }

  async setTenantMediaRoute(
    accessToken: string,
    input: {
      tenantId: string;
      kind: TenantMediaRoute['kind'];
      provider: TenantMediaRoute['provider'];
      status: TenantMediaRoute['status'];
      model: string;
      endpointRegion?: string;
      supportsZdr: boolean;
      supportsMam: boolean;
      providerOptions?: Record<string, unknown>;
      maxUsesDefault?: number;
      requiredDataRegion?: string;
      requireZdr: boolean;
      requireMam: boolean;
      externalApis: 'allow' | 'deny';
      idempotencyKey: string;
    }
  ): Promise<TenantMediaRouteSummary> {
    const path = this.interpolate(SERVICE_ROUTES.mediaRoutesSet.path, {
      tenantId: input.tenantId,
      kind: input.kind,
      provider: input.provider,
    });
    const body = {
      status: input.status,
      model: input.model,
      endpointRegion: input.endpointRegion,
      supportsZdr: input.supportsZdr,
      supportsMam: input.supportsMam,
      providerOptions: input.providerOptions,
      maxUsesDefault: input.maxUsesDefault,
      requiredDataRegion: input.requiredDataRegion,
      requireZdr: input.requireZdr,
      requireMam: input.requireMam,
      externalApis: input.externalApis,
    };
    return (await this.rawCall(
      'PUT',
      path,
      body,
      accessToken,
      input.idempotencyKey
    )) as TenantMediaRouteSummary;
  }

  async testProviderKey(
    accessToken: string,
    input: TestProviderKeyInput
  ): Promise<ProviderKeyTestResult> {
    // Read-style POST — no idempotency key (re-testing must re-probe).
    return (await this.call(
      'providerKeysTest',
      input,
      accessToken
    )) as ProviderKeyTestResult;
  }

  async testLiteLLMConnection(
    accessToken: string,
    input: TestLiteLLMConnectionInput
  ): Promise<ProviderKeyTestResult> {
    return (await this.call(
      'providerKeysLiteLLMTest',
      input,
      accessToken
    )) as ProviderKeyTestResult;
  }

  async getTenantCatalog(
    accessToken: string,
    tenantId: string
  ): Promise<DesktopModelView[]> {
    const path = this.interpolate(SERVICE_ROUTES.tenantCatalogGet.path, {
      tenantId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as DesktopModelView[];
  }

  async getTenantAssignment(
    accessToken: string,
    tenantId: string
  ): Promise<TenantAssignment> {
    const path = this.interpolate(SERVICE_ROUTES.tenantAssignmentGet.path, {
      tenantId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantAssignment;
  }

  async setTenantAssignment(
    accessToken: string,
    input: {
      tenantId: string;
      allowedModelIds: string[];
      defaultMemberModelIds?: string[];
      freeTierModelIds?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantAssignment> {
    const path = this.interpolate(SERVICE_ROUTES.tenantAssignmentPut.path, {
      tenantId: input.tenantId
    });
    return (await this.rawCall(
      'PUT',
      path,
      {
        allowedModelIds: input.allowedModelIds,
        defaultMemberModelIds: input.defaultMemberModelIds,
        freeTierModelIds: input.freeTierModelIds
      },
      accessToken,
      input.idempotencyKey
    )) as TenantAssignment;
  }

  async getCurrentRates(accessToken: string): Promise<CreditRateTableView | undefined> {
    return (await this.call('ratesCurrent', undefined, accessToken)) as CreditRateTableView | undefined;
  }

  async getRateSettings(accessToken: string): Promise<CreditRateSettingsView> {
    return (await this.call('ratesSettingsGet', undefined, accessToken)) as CreditRateSettingsView;
  }

  async putRateSettings(
    accessToken: string,
    input: PutCreditRateSettingsRequest & { idempotencyKey: string }
  ): Promise<PutCreditRateSettingsResult> {
    return (await this.call(
      'ratesSettingsPut',
      input,
      accessToken,
      input.idempotencyKey
    )) as PutCreditRateSettingsResult;
  }

  async adoptPublishedRateSettings(
    accessToken: string,
    idempotencyKey: string
  ): Promise<CreditRateSettingsView> {
    return (await this.call(
      'ratesSettingsAdopt',
      undefined,
      accessToken,
      idempotencyKey
    )) as CreditRateSettingsView;
  }

  // ── platform: partners ─────────────────────────────────────────────

  async listPartners(accessToken: string): Promise<Partner[]> {
    return (await this.call(
      'partnersList',
      undefined,
      accessToken
    )) as Partner[];
  }
  async createPartner(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      tenantCap?: number;
      idempotencyKey: string;
    }
  ): Promise<Partner> {
    return (await this.call(
      'partnersCreate',
      input,
      accessToken,
      input.idempotencyKey
    )) as Partner;
  }
  async getPartner(accessToken: string, partnerId: string): Promise<Partner> {
    const path = this.interpolate(SERVICE_ROUTES.partnerGet.path, {
      partnerId,
    });
    return (await this.rawCall('GET', path, undefined, accessToken)) as Partner;
  }
  async updatePartner(
    accessToken: string,
    input: {
      partnerId: string;
      tenantCap?: number | null;
      status?: 'active' | 'suspended';
      allowCustomModels?: boolean;
      idempotencyKey: string;
    }
  ): Promise<Partner> {
    const path = this.interpolate(SERVICE_ROUTES.partnerPatch.path, {
      partnerId: input.partnerId,
    });
    const { partnerId, idempotencyKey, ...body } = input;
    void partnerId;
    return (await this.rawCall(
      'PATCH',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as Partner;
  }
  async deletePartner(
    accessToken: string,
    input: { partnerId: string; confirmName: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.partnerDelete.path, {
      partnerId: input.partnerId,
    });
    await this.rawCall(
      'DELETE',
      path,
      { confirmName: input.confirmName },
      accessToken,
      input.idempotencyKey
    );
  }
  async setTenantCustomModels(
    accessToken: string,
    input: {
      tenantId: string;
      allowCustomModels: boolean;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(SERVICE_ROUTES.tenantCustomModelsSet.path, {
      tenantId: input.tenantId,
    });
    return (await this.rawCall(
      'PUT',
      path,
      { allowCustomModels: input.allowCustomModels },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }

  async setTenantSelfSignup(
    accessToken: string,
    input: { tenantId: string; allowed?: boolean; enabled?: boolean; idempotencyKey: string }
  ): Promise<TenantSelfSignupResult> {
    const path = this.interpolate(SERVICE_ROUTES.platformTenantSelfSignupSet.path, { tenantId: input.tenantId });
    return (await this.rawCall(
      'PATCH',
      path,
      {
        ...(input.allowed !== undefined ? { allowed: input.allowed } : {}),
        ...(input.enabled !== undefined ? { enabled: input.enabled } : {}),
      },
      accessToken,
      input.idempotencyKey
    )) as TenantSelfSignupResult;
  }
  async getDefaultSignupTenant(
    accessToken: string
  ): Promise<DefaultSignupTenantSetting> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.platformDefaultSignupTenantGet.path,
      undefined,
      accessToken
    )) as DefaultSignupTenantSetting;
  }
  async setDefaultSignupTenant(
    accessToken: string,
    input: { tenantId: string | null; idempotencyKey: string }
  ): Promise<DefaultSignupTenantSetting> {
    return (await this.rawCall(
      'PUT',
      SERVICE_ROUTES.platformDefaultSignupTenantPut.path,
      { tenantId: input.tenantId },
      accessToken,
      input.idempotencyKey
    )) as DefaultSignupTenantSetting;
  }
  async assignTenantCapabilitySource(
    accessToken: string,
    input: { tenantId: string; sourceId: string; idempotencyKey: string }
  ): Promise<CapabilitySourceAssignment> {
    const path = this.interpolate(
      SERVICE_ROUTES.platformTenantCapabilitySourceAssign.path,
      { tenantId: input.tenantId, sourceId: input.sourceId }
    );
    return (await this.rawCall(
      'POST',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    )) as CapabilitySourceAssignment;
  }
  async revokeTenantCapabilitySource(
    accessToken: string,
    input: { tenantId: string; sourceId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(
      SERVICE_ROUTES.platformTenantCapabilitySourceRevoke.path,
      { tenantId: input.tenantId, sourceId: input.sourceId }
    );
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async setTenantCustomerModelLabels(
    accessToken: string,
    input: {
      tenantId: string;
      labels: Record<string, { providerName?: string; modelName?: string }>;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(SERVICE_ROUTES.tenantCustomerModelLabelsSet.path, {
      tenantId: input.tenantId,
    });
    return (await this.rawCall('PUT', path, { labels: input.labels }, accessToken, input.idempotencyKey)) as Tenant;
  }
  async setTenantMcpConnectors(
    accessToken: string,
    input: {
      tenantId: string;
      activeMcpConnectors: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(SERVICE_ROUTES.tenantMcpConnectorsSet.path, {
      tenantId: input.tenantId,
    });
    return (await this.rawCall(
      'PUT',
      path,
      { activeMcpConnectors: input.activeMcpConnectors },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }
  async getTenantMcpConnectors(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMcpConnectors> {
    const path = this.interpolate(SERVICE_ROUTES.tenantMcpConnectorsGet.path, {
      tenantId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantMcpConnectors;
  }
  async getOwnMcpConnectors(accessToken: string): Promise<TenantMcpConnectors> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.ownMcpConnectorsGet.path,
      undefined,
      accessToken
    )) as TenantMcpConnectors;
  }
  async setTenantPermissionModes(
    accessToken: string,
    input: {
      tenantId: string;
      allowedPermissionModes: AgentPermissionMode[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(
      SERVICE_ROUTES.tenantPermissionModesSet.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'PUT',
      path,
      { allowedPermissionModes: input.allowedPermissionModes },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }
  async setTenantDeepAgentApprovalTools(
    accessToken: string,
    input: {
      tenantId: string;
      deepAgentApprovalTools: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(
      SERVICE_ROUTES.tenantDeepAgentApprovalToolsSet.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'PUT',
      path,
      { deepAgentApprovalTools: input.deepAgentApprovalTools },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }
  async getSocialConfig(
    accessToken: string,
    tenantId: string
  ): Promise<TenantSocialConfig> {
    const path = this.interpolate(SERVICE_ROUTES.socialConfigGet.path, {
      tenantId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantSocialConfig;
  }
  async updateSocialMode(
    accessToken: string,
    input: {
      tenantId: string;
      socialLoginMode: SocialLoginMode;
      idempotencyKey: string;
    }
  ): Promise<TenantSocialConfig> {
    const path = this.interpolate(SERVICE_ROUTES.socialConfigSet.path, {
      tenantId: input.tenantId,
    });
    return (await this.rawCall(
      'PATCH',
      path,
      { socialLoginMode: input.socialLoginMode },
      accessToken,
      input.idempotencyKey
    )) as TenantSocialConfig;
  }
  async createSocialProvider(
    accessToken: string,
    input: {
      tenantId: string;
      provider: SocialProviderKind;
      displayName: string;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantSocialProvider> {
    const { tenantId, idempotencyKey, ...body } = input;
    const path = this.interpolate(SERVICE_ROUTES.socialProviderCreate.path, {
      tenantId,
    });
    return (await this.rawCall(
      'POST',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as TenantSocialProvider;
  }
  async updateSocialProvider(
    accessToken: string,
    input: {
      tenantId: string;
      providerId: string;
      displayName?: string;
      enabled?: boolean;
      config?: SocialProviderConfig;
      clientSecret?: string;
      allowJitProvisioning?: boolean;
      allowedEmailDomains?: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantSocialProvider> {
    const { tenantId, providerId, idempotencyKey, ...body } = input;
    const path = this.interpolate(SERVICE_ROUTES.socialProviderUpdate.path, {
      tenantId,
      providerId,
    });
    return (await this.rawCall(
      'PATCH',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as TenantSocialProvider;
  }
  async deleteSocialProvider(
    accessToken: string,
    input: { tenantId: string; providerId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.socialProviderDelete.path, {
      tenantId: input.tenantId,
      providerId: input.providerId,
    });
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async getOrganizationSsoConfig(
    accessToken: string,
    tenantId: string
  ): Promise<TenantOrganizationSsoConfig> {
    const path = this.interpolate(
      SERVICE_ROUTES.organizationSsoConfigGet.path,
      { tenantId }
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantOrganizationSsoConfig;
  }
  async updateOrganizationSsoMode(
    accessToken: string,
    input: {
      tenantId: string;
      mode: OrganizationSsoMode;
      confirmBreakGlass?: boolean;
      idempotencyKey: string;
    }
  ): Promise<TenantOrganizationSsoConfig> {
    const path = this.interpolate(SERVICE_ROUTES.organizationSsoModeSet.path, {
      tenantId: input.tenantId,
    });
    return (await this.rawCall(
      'PATCH',
      path,
      { mode: input.mode, confirmBreakGlass: input.confirmBreakGlass },
      accessToken,
      input.idempotencyKey
    )) as TenantOrganizationSsoConfig;
  }
  async createOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connection: OrganizationSsoConnectionWrite;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin> {
    const path = this.interpolate(
      SERVICE_ROUTES.organizationSsoConnectionCreate.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'POST',
      path,
      input.connection,
      accessToken,
      input.idempotencyKey
    )) as OrganizationSsoConnectionAdmin;
  }
  async updateOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      patch: OrganizationSsoConnectionPatch;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin> {
    const path = this.interpolate(
      SERVICE_ROUTES.organizationSsoConnectionUpdate.path,
      { tenantId: input.tenantId, connectionId: input.connectionId }
    );
    return (await this.rawCall(
      'PATCH',
      path,
      input.patch,
      accessToken,
      input.idempotencyKey
    )) as OrganizationSsoConnectionAdmin;
  }
  async deleteOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      idempotencyKey: string;
    }
  ): Promise<void> {
    const path = this.interpolate(
      SERVICE_ROUTES.organizationSsoConnectionDelete.path,
      input
    );
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async testOrganizationSsoConnection(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      test?: OrganizationSsoConnectionTestInput;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionTestResult> {
    const path = this.interpolate(
      SERVICE_ROUTES.organizationSsoConnectionTest.path,
      { tenantId: input.tenantId, connectionId: input.connectionId }
    );
    return (await this.rawCall(
      'POST',
      path,
      input.test ?? {},
      accessToken,
      input.idempotencyKey
    )) as OrganizationSsoConnectionTestResult;
  }
  async setOrganizationSsoConnectionEnabled(
    accessToken: string,
    input: {
      tenantId: string;
      connectionId: string;
      enabled: boolean;
      idempotencyKey: string;
    }
  ): Promise<OrganizationSsoConnectionAdmin> {
    const route = input.enabled
      ? SERVICE_ROUTES.organizationSsoConnectionEnable
      : SERVICE_ROUTES.organizationSsoConnectionDisable;
    const path = this.interpolate(route.path, {
      tenantId: input.tenantId,
      connectionId: input.connectionId,
    });
    return (await this.rawCall(
      'POST',
      path,
      {},
      accessToken,
      input.idempotencyKey
    )) as OrganizationSsoConnectionAdmin;
  }
  async getOrganizationSsoSamlMetadata(
    accessToken: string,
    tenantId: string,
    connectionId: string
  ): Promise<string> {
    const path = this.interpolate(
      SERVICE_ROUTES.organizationSsoSamlMetadata.path,
      { tenantId, connectionId }
    );
    return this.rawTextCall('GET', path, accessToken);
  }
  async listPartnerAdmins(
    accessToken: string,
    partnerId: string
  ): Promise<PartnerAdminView[]> {
    const path = this.interpolate(SERVICE_ROUTES.partnerAdminsList.path, {
      partnerId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as PartnerAdminView[];
  }
  async grantPartnerAdmin(
    accessToken: string,
    input: {
      partnerId: string;
      email: string;
      name: string;
      password: string;
      idempotencyKey: string;
    }
  ): Promise<{ partnerId: string; userId: string }> {
    const path = this.interpolate(SERVICE_ROUTES.partnerAdminsGrant.path, {
      partnerId: input.partnerId,
    });
    const { partnerId, idempotencyKey, ...body } = input;
    void partnerId;
    return (await this.rawCall(
      'POST',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as { partnerId: string; userId: string };
  }
  async revokePartnerAdmin(
    accessToken: string,
    input: { partnerId: string; userId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.partnerAdminsRevoke.path, {
      partnerId: input.partnerId,
      userId: input.userId,
    });
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async getPartnerModelScope(
    accessToken: string,
    partnerId: string
  ): Promise<string[]> {
    const path = this.interpolate(SERVICE_ROUTES.partnerModelScopeGet.path, {
      partnerId,
    });
    const result = (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as { modelKeys: string[] };
    return result.modelKeys ?? [];
  }
  async setPartnerModelScope(
    accessToken: string,
    input: { partnerId: string; modelKeys: string[]; idempotencyKey: string }
  ): Promise<string[]> {
    const path = this.interpolate(SERVICE_ROUTES.partnerModelScopeSet.path, {
      partnerId: input.partnerId,
    });
    const result = (await this.rawCall(
      'PUT',
      path,
      { modelKeys: input.modelKeys },
      accessToken,
      input.idempotencyKey
    )) as { modelKeys: string[] };
    return result.modelKeys ?? [];
  }
  async listPartnerCustomModels(
    accessToken: string,
    partnerId: string
  ): Promise<PartnerCustomModelSummary[]> {
    const path = this.interpolate(SERVICE_ROUTES.partnerCustomModelsList.path, {
      partnerId,
    });
    const result = (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as { models: PartnerCustomModelSummary[] };
    return result.models ?? [];
  }
  async registerPartnerCustomModel(
    accessToken: string,
    input: {
      partnerId: string;
      definition: PartnerCustomModelWrite;
      idempotencyKey: string;
    }
  ): Promise<PartnerCustomModelSummary> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerCustomModelsCreate.path,
      { partnerId: input.partnerId }
    );
    return (await this.rawCall(
      'POST',
      path,
      { definition: input.definition },
      accessToken,
      input.idempotencyKey
    )) as PartnerCustomModelSummary;
  }
  async patchPartnerCustomModel(
    accessToken: string,
    input: {
      partnerId: string;
      modelKey: string;
      request: UpdatePartnerCustomModelRequest;
      idempotencyKey: string;
    }
  ): Promise<UpdatePartnerCustomModelResult> {
    const path = this.interpolate(SERVICE_ROUTES.partnerCustomModelsPatch.path, {
      partnerId: input.partnerId,
      modelKey: input.modelKey,
    });
    return (await this.rawCall(
      'PATCH',
      path,
      input.request,
      accessToken,
      input.idempotencyKey
    )) as UpdatePartnerCustomModelResult;
  }
  async deletePartnerCustomModel(
    accessToken: string,
    input: { partnerId: string; modelKey: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerCustomModelsDelete.path,
      {
        partnerId: input.partnerId,
        modelKey: input.modelKey,
      }
    );
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async testPartnerCustomModelProvider(
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult> {
    return this.callPartnerCustomModelTest(
      SERVICE_ROUTES.partnerCustomModelsTestProvider.path,
      accessToken,
      input
    );
  }
  async testPartnerCustomModelLlm(
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult> {
    return this.callPartnerCustomModelTest(
      SERVICE_ROUTES.partnerCustomModelsTestLlm.path,
      accessToken,
      input
    );
  }
  /** No idempotency key is passed: a repeat test must reach the network again. */
  private async callPartnerCustomModelTest(
    routePath: string,
    accessToken: string,
    input: { partnerId: string; request: PartnerCustomModelTestRequest }
  ): Promise<PartnerCustomModelTestResult> {
    const path = this.interpolate(routePath, { partnerId: input.partnerId });
    return (await this.rawCall(
      'POST',
      path,
      input.request,
      accessToken
    )) as PartnerCustomModelTestResult;
  }
  async getPartnerMcpConnectorScope(
    accessToken: string,
    partnerId: string
  ): Promise<string[]> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerMcpConnectorScopeGet.path,
      { partnerId }
    );
    const result = (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as { connectorIds: string[] };
    return result.connectorIds ?? [];
  }
  async setPartnerMcpConnectorScope(
    accessToken: string,
    input: {
      partnerId: string;
      connectorIds: string[];
      idempotencyKey: string;
    }
  ): Promise<string[]> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerMcpConnectorScopeSet.path,
      { partnerId: input.partnerId }
    );
    const result = (await this.rawCall(
      'PUT',
      path,
      { connectorIds: input.connectorIds },
      accessToken,
      input.idempotencyKey
    )) as { connectorIds: string[] };
    return result.connectorIds ?? [];
  }

  // ── capability sources (skill-agent-distribution PR10) ──────────────

  async listCapabilitySources(
    accessToken: string
  ): Promise<CapabilitySourceRecord[]> {
    const result = (await this.rawCall(
      'GET',
      SERVICE_ROUTES.capabilitySourcesList.path,
      undefined,
      accessToken
    )) as {
      sources: CapabilitySourceRecord[];
    };
    return result.sources ?? [];
  }
  async registerCapabilitySource(
    accessToken: string,
    input: CapabilitySourceRegisterInput & { idempotencyKey: string }
  ): Promise<CapabilitySourceRecord> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.capabilitySourcesRegister.path,
      body,
      accessToken,
      idempotencyKey
    )) as CapabilitySourceRecord;
  }
  async deleteCapabilitySource(
    accessToken: string,
    id: string,
    idempotencyKey: string
  ): Promise<{ success: boolean }> {
    const path = this.interpolate(SERVICE_ROUTES.capabilitySourceDelete.path, {
      id,
    });
    return (await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      idempotencyKey
    )) as { success: boolean };
  }
  async setPartnerCapabilitySourceScope(
    accessToken: string,
    input: { partnerId: string; sourceIds: string[]; idempotencyKey: string }
  ): Promise<string[]> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerCapabilitySourceScopeSet.path,
      { partnerId: input.partnerId }
    );
    const result = (await this.rawCall(
      'PUT',
      path,
      { sourceIds: input.sourceIds },
      accessToken,
      input.idempotencyKey
    )) as { sourceIds: string[] };
    return result.sourceIds ?? [];
  }

  // ── partner console (scope==='partner') ────────────────────────────

  async partnerLoginWithPassword(input: {
    sessionProtocol?: SessionProtocol;
    email: string;
    password: string;
  }): Promise<TokenPair & { partnerId: string }> {
    return (await this.call('partnerLoginPassword', input)) as TokenPair & {
      partnerId: string;
    };
  }
  async partnerSelf(accessToken: string): Promise<Partner> {
    return (await this.call('partnerMe', undefined, accessToken)) as Partner;
  }
  async partnerOverview(accessToken: string, query?: Pick<UsageQuery, 'from' | 'to'>): Promise<PartnerOverview> {
    return (await this.rawCall('GET', withQuery(SERVICE_ROUTES.partnerOverview.path, query ?? {}), undefined, accessToken)) as PartnerOverview;
  }
  async partnerListAdmins(accessToken: string): Promise<PartnerAdminView[]> {
    return (await this.call('partnerSelfAdminsList', undefined, accessToken)) as PartnerAdminView[];
  }
  async partnerGrantAdmin(accessToken: string, input: { email: string; name: string; password?: string; idempotencyKey: string }): Promise<{ partnerId: string; userId: string }> {
    const { idempotencyKey, ...body } = input;
    return (await this.call('partnerSelfAdminsGrant', body, accessToken, idempotencyKey)) as { partnerId: string; userId: string };
  }
  async partnerRevokeAdmin(accessToken: string, input: { userId: string; idempotencyKey: string }): Promise<void> {
    await this.rawCall('DELETE', this.interpolate(SERVICE_ROUTES.partnerSelfAdminsRevoke.path, input), undefined, accessToken, input.idempotencyKey);
  }
  async partnerSelfModelScope(accessToken: string): Promise<string[]> {
    const result = (await this.call(
      'partnerSelfModelScope',
      undefined,
      accessToken
    )) as { modelKeys: string[] };
    return result.modelKeys ?? [];
  }
  async partnerListCustomModels(
    accessToken: string
  ): Promise<PartnerCustomModelSummary[]> {
    const result = (await this.call(
      'partnerCustomModelsSelfList',
      undefined,
      accessToken
    )) as { models: PartnerCustomModelSummary[] };
    return result.models ?? [];
  }
  async partnerListTenants(accessToken: string): Promise<Tenant[]> {
    return (await this.call(
      'partnerTenantsList',
      undefined,
      accessToken
    )) as Tenant[];
  }
  async partnerCreateTenant(
    accessToken: string,
    input: {
      name: string;
      slug?: string;
      deploymentProfile?: DeploymentProfile;
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    return (await this.call(
      'partnerTenantsCreate',
      input,
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }
  async partnerActAsTenant(
    accessToken: string,
    tenantId: string
  ): Promise<ActAsSessionResult> {
    const path = this.interpolate(SERVICE_ROUTES.partnerActAs.path, {
      tenantId,
    });
    return (await this.rawCall(
      'POST',
      path,
      undefined,
      accessToken
    )) as ActAsSessionResult;
  }
  async partnerGetTenantAssignment(
    accessToken: string,
    tenantId: string
  ): Promise<TenantAssignment> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantAssignmentGet.path,
      { tenantId }
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantAssignment;
  }
  async partnerSetTenantAssignment(
    accessToken: string,
    input: {
      tenantId: string;
      allowedModelIds: string[];
      idempotencyKey: string;
    }
  ): Promise<TenantAssignment> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantAssignmentSet.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'PUT',
      path,
      { modelIds: input.allowedModelIds },
      accessToken,
      input.idempotencyKey
    )) as TenantAssignment;
  }
  async platformDeployCustomModel(
    accessToken: string,
    input: { tenantId: string; customModelKey: string; idempotencyKey: string }
  ): Promise<PartnerCustomModelDeployment> {
    const path = this.interpolate(
      SERVICE_ROUTES.platformTenantCustomModelDeploy.path,
      {
        tenantId: input.tenantId,
        customModelKey: input.customModelKey,
      }
    );
    return (await this.rawCall(
      'POST',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    )) as PartnerCustomModelDeployment;
  }
  async platformRevokeCustomModel(
    accessToken: string,
    input: { tenantId: string; customModelKey: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(
      SERVICE_ROUTES.platformTenantCustomModelRevoke.path,
      {
        tenantId: input.tenantId,
        customModelKey: input.customModelKey,
      }
    );
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async partnerGetTenantMcpConnectors(
    accessToken: string,
    tenantId: string
  ): Promise<TenantMcpConnectors> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantMcpConnectorsGet.path,
      { tenantId }
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as TenantMcpConnectors;
  }
  async partnerSetTenantMcpConnectors(
    accessToken: string,
    input: {
      tenantId: string;
      activeMcpConnectors: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantMcpConnectorsSet.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'PUT',
      path,
      { activeMcpConnectors: input.activeMcpConnectors },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }

  async partnerSetTenantSelfSignup(
    accessToken: string,
    input: { tenantId: string; enabled: boolean; idempotencyKey: string }
  ): Promise<TenantSelfSignupResult> {
    const path = this.interpolate(SERVICE_ROUTES.partnerTenantSelfSignupSet.path, { tenantId: input.tenantId });
    return (await this.rawCall(
      'PATCH', path, { enabled: input.enabled }, accessToken, input.idempotencyKey
    )) as TenantSelfSignupResult;
  }
  async partnerSetTenantPermissionModes(
    accessToken: string,
    input: {
      tenantId: string;
      allowedPermissionModes: AgentPermissionMode[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantPermissionModesSet.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'PUT',
      path,
      { allowedPermissionModes: input.allowedPermissionModes },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }
  async partnerSetTenantDeepAgentApprovalTools(
    accessToken: string,
    input: {
      tenantId: string;
      deepAgentApprovalTools: string[];
      idempotencyKey: string;
    }
  ): Promise<Tenant> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantDeepAgentApprovalToolsSet.path,
      { tenantId: input.tenantId }
    );
    return (await this.rawCall(
      'PUT',
      path,
      { deepAgentApprovalTools: input.deepAgentApprovalTools },
      accessToken,
      input.idempotencyKey
    )) as Tenant;
  }

  // ── credit allocation (Phase 2) ────────────────────────────────────

  async setTenantAllocation(
    accessToken: string,
    input: AllocationInput & { tenantId: string }
  ): Promise<CreditAllocation> {
    const path = this.interpolate(SERVICE_ROUTES.tenantAllocationSet.path, {
      tenantId: input.tenantId,
    });
    const { tenantId, idempotencyKey, ...body } = input;
    void tenantId;
    return (await this.rawCall(
      'PUT',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as CreditAllocation;
  }
  async getTenantAllocation(
    accessToken: string,
    tenantId: string
  ): Promise<AllocationOverview> {
    const path = this.interpolate(SERVICE_ROUTES.tenantAllocationGet.path, {
      tenantId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as AllocationOverview;
  }
  async setPartnerAllocation(
    accessToken: string,
    input: AllocationInput & { partnerId: string }
  ): Promise<CreditAllocation> {
    const path = this.interpolate(SERVICE_ROUTES.partnerAllocationSet.path, {
      partnerId: input.partnerId,
    });
    const { partnerId, idempotencyKey, ...body } = input;
    void partnerId;
    return (await this.rawCall(
      'PUT',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as CreditAllocation;
  }
  async getPartnerAllocation(
    accessToken: string,
    partnerId: string
  ): Promise<AllocationOverview> {
    const path = this.interpolate(SERVICE_ROUTES.partnerAllocationGet.path, {
      partnerId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as AllocationOverview;
  }
  async getPartnerWebSearchUsage(
    accessToken: string,
    partnerId: string,
    query: Pick<UsageQuery, 'from' | 'to'> = {}
  ): Promise<PartnerWebSearchUsageSummary> {
    const path = withQuery(
      this.interpolate(SERVICE_ROUTES.partnerWebSearchUsageGet.path, {
        partnerId,
      }),
      query
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as PartnerWebSearchUsageSummary;
  }
  async partnerGetOwnAllocation(
    accessToken: string
  ): Promise<AllocationOverview> {
    return (await this.call(
      'partnerSelfAllocationGet',
      undefined,
      accessToken
    )) as AllocationOverview;
  }
  async partnerGetOwnWebSearchUsage(
    accessToken: string,
    query: Pick<UsageQuery, 'from' | 'to'> = {}
  ): Promise<PartnerWebSearchUsageSummary> {
    const path = withQuery(
      SERVICE_ROUTES.partnerSelfWebSearchUsageGet.path,
      query
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as PartnerWebSearchUsageSummary;
  }
  async partnerSetTenantAllocation(
    accessToken: string,
    input: AllocationInput & { tenantId: string }
  ): Promise<CreditAllocation> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantAllocationSet.path,
      { tenantId: input.tenantId }
    );
    const { tenantId, idempotencyKey, ...body } = input;
    void tenantId;
    return (await this.rawCall(
      'PUT',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as CreditAllocation;
  }
  async partnerGetTenantAllocation(
    accessToken: string,
    tenantId: string
  ): Promise<AllocationOverview> {
    const path = this.interpolate(
      SERVICE_ROUTES.partnerTenantAllocationGet.path,
      { tenantId }
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as AllocationOverview;
  }

  async tenantGetOwnAllocation(
    accessToken: string
  ): Promise<AllocationOverview> {
    return (await this.call(
      'tenantSelfAllocationGet',
      undefined,
      accessToken
    )) as AllocationOverview;
  }

  async setSubjectAllocation(
    accessToken: string,
    input: AllocationInput & {
      granteeKind: 'group' | 'user';
      granteeId: string;
    }
  ): Promise<CreditAllocation> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'PUT',
      SERVICE_ROUTES.subjectAllocationSet.path,
      body,
      accessToken,
      idempotencyKey
    )) as CreditAllocation;
  }

  async setSubjectAllocationsBatch(
    accessToken: string,
    input: AllocationBatchInput
  ): Promise<AllocationBatchResult> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.subjectAllocationBatchSet.path,
      { items: input.items },
      accessToken,
      input.idempotencyKey
    )) as AllocationBatchResult;
  }

  async getSubjectAllocation(
    accessToken: string,
    granteeKind: 'group' | 'user',
    granteeId: string
  ): Promise<AllocationOverview> {
    const path = this.interpolate(SERVICE_ROUTES.subjectAllocationGet.path, {
      granteeKind,
      granteeId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as AllocationOverview;
  }
  async setMemberQuotaDefault(
    accessToken: string,
    input: MemberQuotaDefaultInput
  ): Promise<MemberQuotaDefault> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'PUT',
      SERVICE_ROUTES.memberQuotaDefaultsSet.path,
      body,
      accessToken,
      idempotencyKey
    )) as MemberQuotaDefault;
  }
  async listCreditLimits(
    accessToken: string
  ): Promise<{ items: CreditLimitView[]; advancedPolicyCount: number }> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.creditLimitsList.path,
      undefined,
      accessToken
    )) as { items: CreditLimitView[]; advancedPolicyCount: number };
  }
  async upsertCreditLimit(
    accessToken: string,
    input: UpsertCreditLimitRequest & { idempotencyKey: string }
  ): Promise<CreditLimitView> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'PUT',
      SERVICE_ROUTES.creditLimitsUpsert.path,
      body,
      accessToken,
      idempotencyKey
    )) as CreditLimitView;
  }
  async previewEffectiveCreditLimits(
    accessToken: string,
    input: EffectiveCreditLimitsRequest
  ): Promise<EffectiveCreditLimitsView> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.creditLimitsPreview.path,
      input,
      accessToken
    )) as EffectiveCreditLimitsView;
  }
  async deleteCreditLimit(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<{ ok: true; deleted: boolean }> {
    // Credit-limit ids contain colons, so they must be percent-encoded into the
    // path segment; the server decodes them back.
    return (await this.rawCall(
      'DELETE',
      SERVICE_ROUTES.creditLimitsDelete.path.replace(
        ':id',
        encodeURIComponent(input.id)
      ),
      undefined,
      accessToken,
      input.idempotencyKey
    )) as { ok: true; deleted: boolean };
  }
  // ── announcements ───────────────────────────────────────────────────────
  // The five operations are implemented ONCE and the surface only picks the
  // route key, so a fix to (say) id encoding cannot land on two surfaces and
  // miss the third.

  private async announcementList(
    routeKey: keyof typeof SERVICE_ROUTES,
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES[routeKey].path, params ?? {}),
      undefined,
      accessToken
    )) as Paginated<Announcement>;
  }

  private async announcementCreate(
    routeKey: keyof typeof SERVICE_ROUTES,
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES[routeKey].path,
      body,
      accessToken,
      idempotencyKey
    )) as Announcement;
  }

  private async announcementPatch(
    routeKey: keyof typeof SERVICE_ROUTES,
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    const { id, idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'PATCH',
      SERVICE_ROUTES[routeKey].path.replace(':id', encodeURIComponent(id)),
      body,
      accessToken,
      idempotencyKey
    )) as Announcement;
  }

  private async announcementTransition(
    routeKey: keyof typeof SERVICE_ROUTES,
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES[routeKey].path.replace(
        ':id',
        encodeURIComponent(input.id)
      ),
      undefined,
      accessToken,
      input.idempotencyKey
    )) as Announcement;
  }

  listAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return this.announcementList('announcementsList', accessToken, params);
  }
  createAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementCreate('announcementsCreate', accessToken, input);
  }
  patchAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementPatch('announcementsPatch', accessToken, input);
  }
  publishAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementTransition(
      'announcementsPublish',
      accessToken,
      input
    );
  }
  archiveAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementTransition(
      'announcementsArchive',
      accessToken,
      input
    );
  }

  listPartnerAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return this.announcementList(
      'partnerAnnouncementsList',
      accessToken,
      params
    );
  }
  createPartnerAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementCreate(
      'partnerAnnouncementsCreate',
      accessToken,
      input
    );
  }
  patchPartnerAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementPatch(
      'partnerAnnouncementsPatch',
      accessToken,
      input
    );
  }
  publishPartnerAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementTransition(
      'partnerAnnouncementsPublish',
      accessToken,
      input
    );
  }
  archivePartnerAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementTransition(
      'partnerAnnouncementsArchive',
      accessToken,
      input
    );
  }

  listPlatformAnnouncements(
    accessToken: string,
    params?: AnnouncementListQuery
  ): Promise<Paginated<Announcement>> {
    return this.announcementList(
      'platformAnnouncementsList',
      accessToken,
      params
    );
  }
  createPlatformAnnouncement(
    accessToken: string,
    input: CreateAnnouncementRequest & { idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementCreate(
      'platformAnnouncementsCreate',
      accessToken,
      input
    );
  }
  patchPlatformAnnouncement(
    accessToken: string,
    input: PatchAnnouncementRequest & { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementPatch(
      'platformAnnouncementsPatch',
      accessToken,
      input
    );
  }
  publishPlatformAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementTransition(
      'platformAnnouncementsPublish',
      accessToken,
      input
    );
  }
  archivePlatformAnnouncement(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<Announcement> {
    return this.announcementTransition(
      'platformAnnouncementsArchive',
      accessToken,
      input
    );
  }

  async listMyAnnouncements(
    accessToken: string,
    params?: { limit?: number; offset?: number }
  ): Promise<MeAnnouncementsResponse> {
    return (await this.rawCall(
      'GET',
      params
        ? withQuery(SERVICE_ROUTES.meAnnouncementsList.path, params)
        : SERVICE_ROUTES.meAnnouncementsList.path,
      undefined,
      accessToken
    )) as MeAnnouncementsResponse;
  }
  async markAnnouncementRead(
    accessToken: string,
    id: string
  ): Promise<{ ok: true }> {
    // No idempotency key: marking read is idempotent server-side (a repeat is a
    // 200, not a replayed write), and `rawCall` only auto-injects one for the
    // admin/platform/partner prefixes.
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.meAnnouncementRead.path.replace(
        ':id',
        encodeURIComponent(id)
      ),
      undefined,
      accessToken
    )) as { ok: true };
  }
  async markAllAnnouncementsRead(
    accessToken: string
  ): Promise<MarkAllAnnouncementsReadResult> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.meAnnouncementsReadAll.path,
      undefined,
      accessToken
    )) as MarkAllAnnouncementsReadResult;
  }

  async listQuotaProfiles(accessToken: string): Promise<QuotaProfileListView> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.quotaProfilesList.path,
      undefined,
      accessToken
    )) as QuotaProfileListView;
  }
  async createQuotaProfile(
    accessToken: string,
    input: CreateQuotaProfileRequest & { idempotencyKey: string }
  ): Promise<QuotaProfileView> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.quotaProfilesCreate.path,
      body,
      accessToken,
      idempotencyKey
    )) as QuotaProfileView;
  }
  async updateQuotaProfile(
    accessToken: string,
    input: UpdateQuotaProfileRequest & { id: string; idempotencyKey: string }
  ): Promise<QuotaProfileView> {
    const { id, idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'PUT',
      SERVICE_ROUTES.quotaProfilesUpdate.path.replace(':id', encodeURIComponent(id)),
      body,
      accessToken,
      idempotencyKey
    )) as QuotaProfileView;
  }
  async deactivateQuotaProfile(
    accessToken: string,
    input: { id: string; idempotencyKey: string }
  ): Promise<QuotaProfileView> {
    return (await this.rawCall(
      'DELETE',
      SERVICE_ROUTES.quotaProfilesDeactivate.path.replace(
        ':id',
        encodeURIComponent(input.id)
      ),
      undefined,
      accessToken,
      input.idempotencyKey
    )) as QuotaProfileView;
  }
  async previewQuotaProfileImpact(
    accessToken: string,
    input: {
      id: string;
      limits?: QuotaProfileLimitInput[];
      status?: 'active' | 'inactive';
    }
  ): Promise<QuotaProfileAssignmentPreview> {
    const { id, ...body } = input;
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.quotaProfilesImpactPreview.path.replace(
        ':id',
        encodeURIComponent(id)
      ),
      body,
      accessToken
    )) as QuotaProfileAssignmentPreview;
  }
  async listUserQuotaAssignments(
    accessToken: string,
    query: UserQuotaAssignmentQuery = {}
  ): Promise<Paginated<UserQuotaAssignmentView>> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.userQuotaAssignmentsList.path, {
        ...query,
        // The server answers the paginated envelope only when a recognized
        // param is present; this method's return type always is that envelope.
        limit: query.limit ?? 50,
      }),
      undefined,
      accessToken
    )) as Paginated<UserQuotaAssignmentView>;
  }
  async assignQuotaProfile(
    accessToken: string,
    input: { userId: string; quotaProfileId: string; idempotencyKey: string }
  ): Promise<MembershipQuotaAssignment> {
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.userQuotaProfileAssign.path.replace(
        ':userId',
        encodeURIComponent(input.userId)
      ),
      { quotaProfileId: input.quotaProfileId },
      accessToken,
      input.idempotencyKey
    )) as MembershipQuotaAssignment;
  }
  async clearQuotaProfilePin(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<MembershipQuotaAssignment | null> {
    return (await this.rawCall(
      'DELETE',
      SERVICE_ROUTES.userQuotaProfileUnpin.path.replace(
        ':userId',
        encodeURIComponent(input.userId)
      ),
      undefined,
      accessToken,
      input.idempotencyKey
    )) as MembershipQuotaAssignment | null;
  }
  async getMemberQuotaDefault(
    accessToken: string,
    nodeKind: MemberQuotaDefault['nodeKind'],
    nodeId: string
  ): Promise<MemberQuotaDefault | null> {
    const path = withQuery(SERVICE_ROUTES.memberQuotaDefaultsGet.path, {
      nodeKind,
      nodeId,
    });
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as MemberQuotaDefault | null;
  }

  // ── admin: read ────────────────────────────────────────────────────

  async overview(accessToken: string): Promise<AdminOverview> {
    return (await this.call(
      'overview',
      undefined,
      accessToken
    )) as AdminOverview;
  }
  async listUsers(accessToken: string): Promise<AdminUserView[]>;
  async listUsers(
    accessToken: string,
    query: UserListQuery
  ): Promise<Paginated<AdminUserView>>;
  async listUsers(
    accessToken: string,
    query?: UserListQuery
  ): Promise<AdminUserView[] | Paginated<AdminUserView>> {
    if (query) {
      const path = withQuery(SERVICE_ROUTES.usersList.path, query);
      return (await this.rawCall(
        'GET',
        path,
        undefined,
        accessToken
      )) as Paginated<AdminUserView>;
    }
    return (await this.call(
      'usersList',
      undefined,
      accessToken
    )) as AdminUserView[];
  }
  async listGatewayBackends(
    accessToken: string
  ): Promise<GatewayBackendAdminView[]> {
    return (await this.call(
      'gatewayBackendsList',
      undefined,
      accessToken
    )) as GatewayBackendAdminView[];
  }
  async listGroups(accessToken: string): Promise<AdminGroupView[]> {
    return (await this.call(
      'groupsList',
      undefined,
      accessToken
    )) as AdminGroupView[];
  }
  async listModelAccess(accessToken: string): Promise<TenantModelAccess[]> {
    return (await this.call(
      'modelAccess',
      undefined,
      accessToken
    )) as TenantModelAccess[];
  }
  async getTenantLoginDefaultModel(accessToken: string): Promise<TenantLoginDefaultModelView> {
    return (await this.call(
      'tenantLoginDefaultModelGet',
      undefined,
      accessToken
    )) as TenantLoginDefaultModelView;
  }
  async previewModels(accessToken: string): Promise<DesktopModelView[]> {
    return (await this.call(
      'modelsPreview',
      undefined,
      accessToken
    )) as DesktopModelView[];
  }
  async listModelRoutes(accessToken: string): Promise<ModelRouteAdminView[]> {
    return (await this.call(
      'modelRoutesList',
      undefined,
      accessToken
    )) as ModelRouteAdminView[];
  }
  async previewRoutes(
    accessToken: string
  ): Promise<{ candidates: ModelRouteAdminView[] }> {
    return (await this.call('routesPreview', undefined, accessToken)) as {
      candidates: ModelRouteAdminView[];
    };
  }
  async listRoutesHealth(
    accessToken: string
  ): Promise<{ backends: Array<{ id: string; status: string }> }> {
    return (await this.call('routesHealth', undefined, accessToken)) as {
      backends: Array<{ id: string; status: string }>;
    };
  }
  async previewPolicy(
    accessToken: string,
    draft: MergePolicyInput
  ): Promise<PolicyContextSnapshot> {
    return (await this.call(
      'policiesPreview',
      draft,
      accessToken
    )) as PolicyContextSnapshot;
  }
  async listAudit(accessToken: string): Promise<AdminAuditEvent[]>;
  async listAudit(
    accessToken: string,
    query: AuditListQuery
  ): Promise<Paginated<AdminAuditEvent>>;
  async listAudit(
    accessToken: string,
    query?: AuditListQuery
  ): Promise<AdminAuditEvent[] | Paginated<AdminAuditEvent>> {
    if (query) {
      const path = withQuery(SERVICE_ROUTES.audit.path, query);
      return (await this.rawCall(
        'GET',
        path,
        undefined,
        accessToken
      )) as Paginated<AdminAuditEvent>;
    }
    return (await this.call(
      'audit',
      undefined,
      accessToken
    )) as AdminAuditEvent[];
  }
  async exportAudit(accessToken: string): Promise<AdminAuditEvent[]> {
    // Phase 3 exports reuse the read-only `audit` endpoint with elevated
    // RBAC; the server-side `exportAudit` method enforces `admin.audit.export`.
    return (await this.call(
      'audit',
      undefined,
      accessToken
    )) as AdminAuditEvent[];
  }
  async listQuotas(accessToken: string): Promise<QuotaPolicy[]> {
    return (await this.call(
      'quotasList',
      undefined,
      accessToken
    )) as QuotaPolicy[];
  }
  async usageSummary(
    accessToken: string,
    query: UsageQuery = {}
  ): Promise<UsageSummaryView> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.usageSummary.path, query),
      undefined,
      accessToken
    )) as UsageSummaryView;
  }
  async usageSeries(
    accessToken: string,
    query: UsageSeriesQuery = {}
  ): Promise<UsageSeriesPoint[]> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.usageSeries.path, query),
      undefined,
      accessToken
    )) as UsageSeriesPoint[];
  }
  async usageEvents(
    accessToken: string,
    query: UsageQuery = {}
  ): Promise<Paginated<UsageEventView>> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.usageEvents.path, query),
      undefined,
      accessToken
    )) as Paginated<UsageEventView>;
  }
  async usageVerify(
    accessToken: string,
    query: UsageQuery = {}
  ): Promise<UsageChainVerification> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.usageVerify.path, query),
      undefined,
      accessToken
    )) as UsageChainVerification;
  }
  async usageExport(
    accessToken: string,
    query: UsageQuery = {}
  ): Promise<string> {
    return this.rawTextCall(
      'GET',
      withQuery(SERVICE_ROUTES.usageExport.path, query),
      accessToken
    );
  }

  async creditSettlement(
    accessToken: string,
    query: CreditSettlementQuery = {}
  ): Promise<CreditSettlementReport> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.creditSettlement.path, query),
      undefined,
      accessToken
    )) as CreditSettlementReport;
  }

  async creditSettlementExport(
    accessToken: string,
    query: CreditSettlementQuery = {}
  ): Promise<string> {
    return this.rawTextCall(
      'GET',
      withQuery(SERVICE_ROUTES.creditSettlementExport.path, query),
      accessToken
    );
  }

  // ── admin: write ───────────────────────────────────────────────────

  async inviteUser(
    accessToken: string,
    input: {
      email: string;
      name: string;
      roles?: ServiceRole[];
      idempotencyKey: string;
    }
  ): Promise<InvitedUser> {
    return (await this.call('usersInvite', input, accessToken)) as InvitedUser;
  }
  async createUserWithPassword(
    accessToken: string,
    input: {
      email: string;
      name: string;
      password: string;
      roles?: ServiceRole[];
      idempotencyKey: string;
    }
  ): Promise<CreatedUserResult> {
    // Same route as the invite flow (POST /v1/admin/users); the presence of a
    // `password` field makes the service-api provision the user directly.
    return (await this.call(
      'usersInvite',
      input,
      accessToken
    )) as CreatedUserResult;
  }
  async updateUserRoles(
    accessToken: string,
    input: { userId: string; roles: ServiceRole[]; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.usersPatch.path, {
      id: input.userId,
    });
    await this.rawCall(
      'PATCH',
      path,
      { roles: input.roles },
      accessToken,
      input.idempotencyKey
    );
  }
  async disableUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.usersPatch.path, {
      id: input.userId,
    });
    await this.rawCall(
      'PATCH',
      path,
      { status: 'disabled' },
      accessToken,
      input.idempotencyKey
    );
  }
  async enableUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.usersPatch.path, {
      id: input.userId,
    });
    await this.rawCall(
      'PATCH',
      path,
      { status: 'active' },
      accessToken,
      input.idempotencyKey
    );
  }
  async deleteUser(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.usersDelete.path, {
      id: input.userId,
    });
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async grantPlatformAdmin(
    accessToken: string,
    input: {
      userId?: string;
      email?: string;
      name?: string;
      password?: string;
      idempotencyKey: string;
    }
  ): Promise<{ userId: string }> {
    const { idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'POST',
      SERVICE_ROUTES.platformAdminsGrant.path,
      body,
      accessToken,
      idempotencyKey
    )) as { userId: string };
  }
  async listPlatformAdmins(accessToken: string): Promise<PlatformAdminView[]> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.platformAdminsList.path,
      undefined,
      accessToken
    )) as PlatformAdminView[];
  }
  async revokePlatformAdmin(
    accessToken: string,
    input: { userId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.platformAdminsRevoke.path, {
      userId: input.userId,
    });
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async assignSeat(
    accessToken: string,
    input: {
      userId: string;
      seatStatus: 'assigned' | 'unassigned' | 'suspended';
      idempotencyKey: string;
    }
  ): Promise<void> {
    await this.call('seats', input, accessToken, input.idempotencyKey);
  }
  async updateDownloadAccess(
    accessToken: string,
    input: {
      userId: string;
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
      idempotencyKey: string;
    }
  ): Promise<void> {
    // Admin download-access update is the same wire as `updateDownloadChannels`
    // (server endpoint `PUT /v1/admin/downloads/channels`); the client exposes
    // a single canonical method under the shorter name to match the service
    // route and avoid duplicate route names.
    await this.call(
      'downloadChannelsUpdate',
      input,
      accessToken,
      input.idempotencyKey
    );
  }
  async createGroup(
    accessToken: string,
    input: { name: string; idempotencyKey: string }
  ): Promise<Group> {
    return (await this.call(
      'groupsCreate',
      input,
      accessToken,
      input.idempotencyKey
    )) as Group;
  }
  async updateGroupMembers(
    accessToken: string,
    input: {
      groupId: string;
      addUserId?: string;
      removeUserId?: string;
      idempotencyKey: string;
    }
  ): Promise<Group> {
    const path = this.interpolate(SERVICE_ROUTES.groupsPatch.path, {
      id: input.groupId,
    });
    const { groupId, idempotencyKey, ...body } = input;
    return (await this.rawCall(
      'PATCH',
      path,
      body,
      accessToken,
      idempotencyKey
    )) as Group;
  }
  async updateAllowlist(
    accessToken: string,
    input: {
      subjectType: 'user' | 'group';
      subjectId: string;
      allowedModelIds: string[];
      defaultModelId: string;
      idempotencyKey: string;
    }
  ): Promise<TenantModelAccess> {
    return (await this.call(
      'allowlist',
      input,
      accessToken,
      input.idempotencyKey
    )) as TenantModelAccess;
  }
  async updateTenantLoginDefaultModel(
    accessToken: string,
    input: { loginDefaultModelId: string | null; idempotencyKey: string }
  ): Promise<TenantLoginDefaultModelView> {
    return (await this.call(
      'tenantLoginDefaultModelPut',
      { loginDefaultModelId: input.loginDefaultModelId },
      accessToken,
      input.idempotencyKey
    )) as TenantLoginDefaultModelView;
  }
  async setCustomerModelLabels(
    accessToken: string,
    input: { labels: Record<string, { providerName?: string; modelName?: string }>; idempotencyKey: string }
  ): Promise<Tenant> {
    return (await this.call('customerModelLabels', input, accessToken, input.idempotencyKey)) as Tenant;
  }
  async createModelRoute(
    accessToken: string,
    input: {
      route: ModelRoute;
      catalogItem: VelugaModelCatalogItem;
      idempotencyKey: string;
    }
  ): Promise<ModelRouteAdminView> {
    return (await this.call(
      'modelRoutesCreate',
      input,
      accessToken,
      input.idempotencyKey
    )) as ModelRouteAdminView;
  }
  async removeCustomProviderModel(
    accessToken: string,
    input: { catalogModelId: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.customModelRemove.path, {
      catalogModelId: input.catalogModelId,
    });
    await this.rawCall(
      'DELETE',
      path,
      undefined,
      accessToken,
      input.idempotencyKey
    );
  }
  async createGatewayBackend(
    accessToken: string,
    input: { backend: GatewayBackend; idempotencyKey: string }
  ): Promise<void> {
    await this.call(
      'gatewayBackendsCreate',
      input,
      accessToken,
      input.idempotencyKey
    );
  }
  async patchGatewayBackend(
    accessToken: string,
    input: {
      backendId: string;
      backend: Partial<GatewayBackend>;
      idempotencyKey: string;
    }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.gatewayBackendsPatch.path, {
      id: input.backendId,
    });
    const { backendId, idempotencyKey, ...body } = input;
    await this.rawCall(
      'PATCH',
      path,
      { backend: body.backend },
      accessToken,
      idempotencyKey
    );
  }
  async updateRoutingPolicy(
    accessToken: string,
    input: {
      modelId: string;
      routing: ModelRoutingPolicy;
      idempotencyKey: string;
    }
  ): Promise<void> {
    await this.call('routingPolicy', input, accessToken, input.idempotencyKey);
  }
  async setQuota(
    accessToken: string,
    input: { quota: QuotaPolicy; idempotencyKey: string }
  ): Promise<void> {
    await this.call('quotas', input, accessToken, input.idempotencyKey);
  }
  async updateDownloadChannels(
    accessToken: string,
    input: {
      userId: string;
      desktopAccess?: boolean;
      downloadChannels?: DownloadChannel[];
      allowedPlatforms?: DesktopPlatform[];
      minimumDesktopVersion?: string;
      idempotencyKey: string;
    }
  ): Promise<unknown> {
    return await this.call(
      'downloadChannelsUpdate',
      input,
      accessToken,
      input.idempotencyKey
    );
  }
  async downloadLatestForUser(
    accessToken: string,
    input: {
      userId: string;
      channel: DownloadChannel;
      platform: DesktopPlatform;
    }
  ): Promise<DownloadManifest> {
    const path = this.interpolate(
      SERVICE_ROUTES.downloadLatestForUser.path,
      input
    );
    return (await this.rawCall(
      'GET',
      path,
      undefined,
      accessToken
    )) as DownloadManifest;
  }
  async listExtensionReleases(accessToken: string, extensionId: string, platform: 'win32' | 'darwin'): Promise<ExtensionReleaseView[]> {
    const path = this.interpolate(SERVICE_ROUTES.extensionReleasesList.path, { extensionId, platform });
    return (await this.rawCall('GET', path, undefined, accessToken)) as ExtensionReleaseView[];
  }
  async promoteExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; version: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.extensionReleasePromote.path, input);
    await this.rawCall('POST', path, {}, accessToken, input.idempotencyKey);
  }
  async rollbackExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; version: string; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.extensionReleaseRollback.path, input);
    await this.rawCall('POST', path, { version: input.version }, accessToken, input.idempotencyKey);
  }
  async withdrawExtensionRelease(
    accessToken: string,
    input: { extensionId: string; platform: 'win32' | 'darwin'; idempotencyKey: string }
  ): Promise<void> {
    const path = this.interpolate(SERVICE_ROUTES.extensionReleaseWithdraw.path, input);
    await this.rawCall('POST', path, {}, accessToken, input.idempotencyKey);
  }

  // ── internals ──────────────────────────────────────────────────────

  /**
   * Look up the route by name, optionally substitute path params from the
   * body, and call the wire. We do NOT auto-extract path params from the
   * call args because each call site does it explicitly to keep body
   * semantics clear (e.g. `usersPatch` PATCHes `{roles}` only, while the
   * `:id` is sourced from the typed arg).
   */
  private async call(
    routeName: keyof typeof SERVICE_ROUTES,
    body: unknown,
    accessTokenArg?: string,
    idempotencyKeyArg?: string
  ): Promise<unknown> {
    const route = SERVICE_ROUTES[routeName as keyof typeof SERVICE_ROUTES];
    const token = accessTokenArg ?? this.opts.getAccessToken();
    return await this.rawCall(
      route.method,
      route.path,
      body,
      token,
      idempotencyKeyArg
    );
  }

  private async rawCall(
    method: string,
    path: string,
    body: unknown,
    accessToken: string | undefined,
    idempotencyKey?: string,
    extraHeaders: Record<string, string> = {},
    requestSignal?: AbortSignal
  ): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json', ...extraHeaders };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;
    // Belt-and-suspenders: also auto-inject idempotency-key for admin writes
    // that come through this path WITHOUT an explicit key — the service-api
    // would 400 without it.
    if (
      !idempotencyKey &&
      method !== 'GET' &&
      (path.startsWith('/v1/admin/') ||
        path.startsWith('/v1/platform/') ||
        path.startsWith('/v1/partner/')) &&
      !path.endsWith('/audit')
    ) {
      headers['idempotency-key'] = this.randomUUID();
    }

    const url = `${this.opts.baseUrl.replace(/\/+$/, '')}${path}`;
    const response = await this.fetcher(url, {
      method,
      headers,
      signal: requestSignal ?? AbortSignal.timeout(this.opts.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (response.status === 204) return undefined;
    const payload = await response.json().catch((error: unknown) => {
      if (requestSignal?.aborted) throw error;
      return undefined;
    });
    requestSignal?.throwIfAborted();
    if (!response.ok)
      throw new ServiceClientError(
        response.status,
        payload ?? { error: response.statusText }
      );
    return payload;
  }

  private async rawTextCall(
    method: string,
    path: string,
    accessToken: string | undefined
  ): Promise<string> {
    const headers: Record<string, string> = { accept: 'text/csv' };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    const response = await this.fetcher(
      `${this.opts.baseUrl.replace(/\/+$/, '')}${path}`,
      {
        method,
        headers,
        signal: AbortSignal.timeout(this.opts.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS),
      }
    );
    const text = await response.text();
    if (!response.ok) throw new ServiceClientError(response.status, text);
    return text;
  }
  async platformDiagnosticBundles(
    accessToken: string,
    query: DiagnosticBundleListQuery = {}
  ): Promise<Paginated<DiagnosticBundleView>> {
    return (await this.rawCall(
      'GET',
      withQuery(SERVICE_ROUTES.platformDiagnosticBundlesList.path, query),
      undefined,
      accessToken
    )) as Paginated<DiagnosticBundleView>;
  }
  async platformDiagnosticBundleDownload(
    accessToken: string,
    id: string
  ): Promise<{ url: string; expiresAt: string }> {
    return (await this.rawCall(
      'GET',
      SERVICE_ROUTES.platformDiagnosticBundleDownload.path.replace(
        ':id',
        encodeURIComponent(id)
      ),
      undefined,
      accessToken
    )) as { url: string; expiresAt: string };
  }

  private async rawBinaryCall(
    method: string,
    path: string,
    bytes: Uint8Array,
    accessToken: string,
    extraHeaders: Record<string, string>,
    timeoutMs: number
  ): Promise<unknown> {
    const headers: Record<string, string> = {
      accept: 'application/json',
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/zip',
      ...extraHeaders
    };
    const response = await this.fetcher(`${this.opts.baseUrl.replace(/\/+$/, '')}${path}`, {
      method,
      headers,
      body: bytes as BodyInit,
      signal: AbortSignal.timeout(timeoutMs)
    });
    const payload = await response.json().catch(() => undefined);
    if (!response.ok) throw new ServiceClientError(response.status, payload ?? { error: response.statusText });
    return payload;
  }

  private interpolate(
    path: string,
    params: Record<string, string | undefined>
  ): string {
    return path.replace(/:([a-zA-Z_]+)/g, (_, key: string) => {
      const value = params[key];
      if (value === undefined) throw new Error(`missing path param :${key}`);
      return encodeURIComponent(value);
    });
  }
}

/** Keep the deprecated derived view referenced until its compatibility cleanup. */
const _ADMIN_WRITE_ROUTES_REF: ReadonlySet<string> = ADMIN_WRITE_ROUTES;
void _ADMIN_WRITE_ROUTES_REF;
